import { blockOf, type ToolKind } from './registry/blocks.ts';
import { itemById } from './registry/items.ts';
import type { ItemStack } from './inventory.ts';

/**
 * Mining speed and harvest rules, following vanilla's formula.
 *
 * damage-per-tick = speed / hardness / (canHarvest ? 30 : 100)
 *
 * where `speed` is the tool's multiplier if it is the right kind of tool and 1
 * otherwise. The block breaks when the accumulated damage reaches 1, so an
 * unbreakable-by-hand block still yields eventually but drops nothing unless
 * the correct tool tier was used.
 */

export interface MiningInfo {
  /** Ticks to break, or Infinity for unbreakable. */
  ticks: number;
  /** Whether the block will drop its item. */
  harvestable: boolean;
  /** Damage accumulated per tick. */
  perTick: number;
}

export function toolSpeedAgainst(held: ItemStack | null, blockTool: ToolKind): { speed: number; tier: number; kind: ToolKind } {
  if (!held) return { speed: 1, tier: 0, kind: 'none' };
  const def = itemById(held.item);
  if (!def?.tool) return { speed: 1, tier: 0, kind: 'none' };
  const matches = def.tool.kind === blockTool;
  return { speed: matches ? def.tool.speed : 1, tier: def.tool.tier, kind: def.tool.kind };
}

export function miningInfo(state: number, held: ItemStack | null): MiningInfo {
  const def = blockOf(state);
  const hardness = def.hardness ?? 0;
  if (hardness < 0) return { ticks: Infinity, harvestable: false, perTick: 0 };
  if (hardness === 0) return { ticks: 0, harvestable: true, perTick: 1 };

  const blockTool = def.tool ?? 'none';
  const { speed, tier, kind } = toolSpeedAgainst(held, blockTool);

  // A block that "requires" a tool drops nothing unless the right kind of tool
  // of at least the required tier is used. Blocks without the flag always drop.
  const correctKind = blockTool === 'none' || kind === blockTool;
  const requiredTier = def.tier ?? 0;
  const harvestable = def.requiresTool ? correctKind && tier >= requiredTier : true;

  const perTick = speed / hardness / (harvestable ? 30 : 100);
  const ticks = Math.max(1, Math.ceil(1 / perTick));
  return { ticks, harvestable, perTick };
}

/** Break progress 0..1 after `ticks` ticks of mining. */
export function breakProgress(state: number, held: ItemStack | null, ticks: number): number {
  const info = miningInfo(state, held);
  if (info.ticks === Infinity) return 0;
  if (info.ticks === 0) return 1;
  return Math.min(1, ticks / info.ticks);
}

/** True when the player is allowed to break this block at all. */
export function canBreak(state: number): boolean {
  const def = blockOf(state);
  return (def.hardness ?? 0) >= 0;
}
